import type {
  ColDef,
  ICellRendererParams,
  RowClickedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { DateTime } from 'luxon';
import { type FC, useState } from 'react';
import { Badge, OverlayTrigger, Popover } from 'react-bootstrap';
import { Eye, PencilSquare, PersonCheck, PersonX } from 'react-bootstrap-icons';
import VerticalDotMenuIcon from '../../../assets/vertical-dot-menu.svg';
import { config } from '../../../modules/config';
import { TemporaryAccessCodeStatus } from '../../../modules/enums/status';
import type { TemporaryAccessCodes } from '../../../modules/interfaces';
import { DataTable } from '../DataTable';

type TemporaryAccessCodeTableProps = {
  tableData: any;
  onViewAccessCode: (temporaryAccessCode?: TemporaryAccessCodes) => void;
  onRefresh: (temporaryAccessCode?: TemporaryAccessCodes) => void;
  onEditTemporaryAccessCode: (temporaryAccessCode?: TemporaryAccessCodes) => void;
  addButtonFunction?: () => void;
  onDisable: (temporaryAccessCode?: TemporaryAccessCodes) => void;
  addButtonText?: string;
};

export const statusColorMapper: Record<string, string> = {
  ENABLED: 'primary',
  DISABLED: 'danger',
};

export const TemporaryAccessCodeTable: FC<TemporaryAccessCodeTableProps> = ({
  tableData,

  onViewAccessCode,
  onEditTemporaryAccessCode,
  addButtonFunction,
  onDisable,
  addButtonText,
}: TemporaryAccessCodeTableProps) => {
  const renderMenuIcon = (item: RowClickedEvent<TemporaryAccessCodes>) => {
    return (
      <div className="menu-icon-container-style">
        <OverlayTrigger
          trigger="click"
          placement="left"
          rootClose
          overlay={
            <Popover id="popover-basic">
              <Popover.Body>
                <div
                  style={{ marginTop: '10px' }}
                  className="edit-damage-report-container"
                  onClick={(e) => {
                    document.body.click();
                    onViewAccessCode(item.data);
                  }}
                >
                  <Eye size={20} />
                  <div className="edit-text-style">View code</div>
                </div>

                <div
                  style={{ marginTop: '10px' }}
                  className="edit-damage-report-container"
                  onClick={(e) => {
                    document.body.click();
                    onEditTemporaryAccessCode(item.data);
                  }}
                >
                  <PencilSquare size={20} />
                  <div className="edit-text-style">Edit</div>
                </div>

                <div
                  onClick={(e) => {
                    document.body.click();
                    onDisable(item?.data);
                  }}
                  style={{ paddingTop: '8px', paddingBottom: '8px' }}
                  className="edit-damage-report-container"
                >
                  {item?.data?.status === TemporaryAccessCodeStatus.ENABLED ? (
                    <PersonX size={20} />
                  ) : (
                    <PersonCheck size={20} />
                  )}
                  <div className="edit-text-style">
                    {item?.data?.status === TemporaryAccessCodeStatus.DISABLED
                      ? 'Enable'
                      : 'Disable'}
                  </div>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <div>
            <img src={VerticalDotMenuIcon} alt="vertical-icon" />
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  const BadgeCellRenderer: FC<ICellRendererParams> = (params: ICellRendererParams) => {
    const { value } = params;

    return (
      <Badge
        className="status-badge"
        bg={statusColorMapper[value?.toUpperCase() as string]}
      >
        {value}
      </Badge>
    );
  };

  const [columns] = useState<ColDef<TemporaryAccessCodes>[]>([
    {
      field: 'duration',
      headerName: 'Duration',
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<TemporaryAccessCodes, string>) => {
        const { value } = params;
        if (!value) return '';
        return `${params.data?.duration}${params.data?.durationUnit}`;
      },
      cellStyle: {
        color: '#757474',
      },
      flex: 2,
    },
    { field: 'guestEmail', headerName: 'Guest Email', flex: 4 },

    {
      field: 'createdAt',
      headerName: 'Start Date',
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<TemporaryAccessCodes, string>) => {
        const { value } = params;
        if (!value) return '';
        const formattedDate = DateTime.fromISO(value).toFormat('MM-dd-yyyy HH:mm');
        return formattedDate;
      },
      cellStyle: {
        color: '#757474',
      },
      flex: 2,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<TemporaryAccessCodes, string>) => {
        const { value } = params;
        if (!value) return '';
        const formattedDate = DateTime.fromISO(value).toFormat('MM-dd-yyyy HH:mm');
        return formattedDate;
      },
      cellStyle: {
        color: '#757474',
      },
      flex: 2,
    },
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer: BadgeCellRenderer,
      maxWidth: 160,
      sortable: true,
    },

    {
      headerName: 'ACTIONS',
      cellRenderer: renderMenuIcon,
      flex: 1,
    },
  ]);

  return (
    <DataTable
      addButtonFunction={addButtonFunction}
      addButtonText={addButtonText}
      component="temporary_access_codes"
      searchString=""
      changeState={0}
      rowData={tableData}
      columnDefs={columns}
      pagination
      paginationPageSize={config.tables.reservations.paginationSize}
    />
  );
};
